import { PartnerType } from "@/app/utils/partners.enum";
import { useAtom } from "jotai";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { addressFormSchema } from "./components/AddressForm";
import { bankFormSchema } from "./components/BankInfoForm";
import { personalFormSchema } from "./components/PersonalInfoForm";
import { currentStep } from "../../../../../app/store";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { formatZipCode } from "../../../../../app/utils/formatZipCode";
import { formatCpfNumber } from "../../../../../app/utils/cpfFormater";
import { useToast } from "../../../../components/ui/use-toast";
import { customerService } from "../../../../../app/services/customerService";
import { createCustomerBody } from "../../../../../app/services/customerService/createCustomer";
import { formatDateForInput } from "../../../../../app/utils/formatDate";
import { CustomerInfo } from "@/app/entities/CustomerInfo";
import { Table } from "@/app/services/transactionService/getAvailableCredit";
import { transactionsService } from "@/app/services/transactionService";
import { useAuth } from "@/app/hooks/useAuth";
import { parse } from "date-fns";
import { cltService } from "@/app/services/cltService";
import { ITableClt } from "@/app/services/cltService/getTables";

const formSchema = z.object({
  address: addressFormSchema,
  bankAccount: bankFormSchema,
  personalInfo: personalFormSchema,
});

export type formData = z.infer<typeof formSchema>;

interface CreateTransactionParams {
  customerId: string;
  pin?: string[] | null;
}

export default function useNewWithdrawModal(
  table: Table | null | ITableClt,
  type: "FGTS" | "CLT",
  matricula?: number,
  cnpjEmpresa?: number,
) {
  const { cpf } = useParams();
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [contractClt, setContractClt] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [step, setStep] = useAtom(currentStep);
  const form = useForm<formData>({ resolver: zodResolver(formSchema) });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const customer = queryClient.getQueryData<CustomerInfo | undefined>([
    "getCustomer",
    cpf,
  ]);
  const { user } = useAuth();

  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting },
  } = form;

  const clientName = useMemo(() => {
    return getValues("personalInfo.name");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues, step]);

  useEffect(() => {
    if (customer) {
      reset({
        address: {
          ...customer.address,
          zipCode: formatZipCode(customer?.address?.zipCode),
          number: customer?.address?.number,
        },
        personalInfo: {
          ...customer,
          birthDate: customer?.birthDate
            ? formatDateForInput(customer.birthDate)
            : undefined,
          cpf: formatCpfNumber(customer.cpf.toString()),
          rg: {
            ...customer.rg,
            uf: customer?.rg?.uf?.toUpperCase(),
            date: customer?.rg?.date
              ? formatDateForInput(customer.rg.date)
              : undefined,
          },
        },
      });
    } else {
      reset({
        personalInfo: {
          cpf: formatCpfNumber(cpf ?? ""),
        },
      });
    }
    setStep(0);
  }, [cpf, customer, reset, setStep]);

  const { mutateAsync: createTransactionCLT } = useMutation({
    mutationFn: async (customerId: string) => {
      const cltTable = table as ITableClt | null;
      if (cltTable) {
        return await cltService.createTransaction({
          customerId,
          value: cltTable.ValorLiquidoCliente ?? 0,
          valorParcela: cltTable.ValorParcela ?? 0,
          taxa: cltTable.Taxa ?? 0,
          Prazo: cltTable.Prazo ?? 0,
          codigoTabela: cltTable.CodigoTabela ?? 0,
          tableCode: cltTable.tableCode,
          codigoEmpregador: cltTable.CodigoEmpregador ?? 0,
          codigoOrgao: cltTable.CodigoOrgao ?? 0,
          matricula: matricula ?? 0,
          cnpjEmpresa: cnpjEmpresa ?? 0,
        });
      }
    },
  });

  const { mutateAsync: createTransaction } = useMutation({
    mutationFn: async ({ customerId, pin }: CreateTransactionParams) => {
      if (table) {
        const value = table.ValorLiquidoCliente * 100;
        const codigoTabela = table.CodigoTabela;
        const codigoProduto = table.CodigoProduto;
        const codigoEmpregador = table.CodigoEmpregador;
        const codigoOrg = table.CodigoOrgao;
        const prazo = table.Prazo;

        const pinJoin = pin ? pin.join("") : "";

        return await transactionsService.createTransaction({
          value,
          CodigoOrg: Number(codigoOrg),
          customerId,
          CodigoEmpregador: Number(codigoEmpregador),
          CodigoProduto: Number(codigoProduto),
          CodigoTabela: String(codigoTabela),
          Prazo: Number(prazo),
          pin: pinJoin || undefined,
        });
      }
    },
  });

  const { mutateAsync: createCustomer } = useMutation({
    mutationFn: async (data: createCustomerBody) =>
      await customerService.createCustomer(data, PartnerType.FGTS),
  });

  const handleOpenConfirmation = () => {
    setIsOpenConfirmModal(true);
  };

  const submitForm = handleSubmit(async (dataform: formData) => {
    try {
      setIsOpenConfirmModal(false);
      if (!cpf) throw new Error("cpf is undefined");

      if (user?.isPinProtected && pin.length !== 4) {
        toast({
          variant: "destructive",
          title: "PIN inválido",
          description: "Por favor, insira um PIN de 4 dígitos",
        });
        return;
      }

      const customerResponse = await createCustomer({
        address: {
          ...dataform.address,
          zipCode: dataform.address.zipCode.replace(/\D/g, ""),
          number:
            dataform.address.number && dataform.address.number.trim() !== ""
              ? dataform.address.number
              : "S/N",
        },
        bankAccount: dataform.bankAccount,
        ...dataform.personalInfo,
        birthDate: parse(
          dataform.personalInfo.birthDate,
          "dd/MM/yyyy",
          new Date(),
        ).toISOString(),
        rg: {
          ...dataform.personalInfo.rg,
          date: parse(
            dataform.personalInfo.rg.date,
            "dd/MM/yyyy",
            new Date(),
          ).toISOString(),
        },

        cpf,
      });

      if (!customerResponse?._id) {
        throw new Error("Falha ao criar cliente");
      }

      let transaction: { _id: string; contract?: string } | undefined;
      if (type === "CLT") {
        transaction = await createTransactionCLT(customerResponse?._id);
        setContractClt(transaction?.contract ?? "");
      } else {
        transaction = await createTransaction({
          customerId: customerResponse?._id,
          pin: user?.isPinProtected ? pin : undefined,
        });
      }

      if (!transaction?._id) {
        throw new Error("Falha ao criar transação");
      }

      setStep((state) => state + 1);
      toast({
        title: "Proposta enviada com sucesso!",
      });
    } catch (e: unknown) {
      const error = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };

      console.error(error);
      toast({
        variant: "destructive",
        title:
          error?.response?.data?.message ||
          error.message ||
          "Falha ao criar transação, tente novamente mais tarde",
      });
      setStep(3);
    }
  });

  const steps = {
    personalInfo: step === 0,
    address: step === 1,
    bankInfo: step === 2,
    review: step === 3,
    sentProposal: step === 4,
  };

  const title = [
    "Informações do cliente",
    "Endereço do cliente",
    "Dados bancários do cliente",
    "Dados da proposta",
  ];

  return {
    submitForm,
    title,
    steps,
    form,
    step,
    clientName,
    isSubmitting,
    pin,
    setPin,
    isOpenConfirmModal,
    setIsOpenConfirmModal,
    handleOpenConfirmation,
    user,
    contractClt,
  };
}
